import React from 'react';
import { Star, Calendar, Camera, Edit, Trash2 } from 'lucide-react';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import { ClientEmptyState } from '../../components/client/guidance';
import { ListItemSkeleton } from '../../components/shared/ClientPageSkeleton';
import { useClientReviews, useDeleteReview } from '../../services/reviewService';

const REVIEWS_SUBTITLE = 'Feedback you have left for vendors after completed events.';

const ClientReviews = () => {
  const { data: reviewsResponse, isLoading, error } = useClientReviews({ per_page: 50 });
  const deleteReviewMutation = useDeleteReview();

  // Extract reviews from response
  const paginatedData = reviewsResponse?.data;
  const reviews = Array.isArray(paginatedData?.data) 
    ? paginatedData.data 
    : Array.isArray(paginatedData) 
    ? paginatedData 
    : [];

  const handleDeleteReview = async (id) => {
    if (window.confirm('Are you sure you want to delete this review? This action cannot be undone.')) {
      try {
        await deleteReviewMutation.mutateAsync(id);
      } catch (error) {
        console.error('Failed to delete review:', error);
        alert('Failed to delete review. Please try again.');
      }
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${
          i < rating
            ? 'text-amber-400 fill-amber-400'
            : 'text-gray-300 dark:text-gray-600'
        }`}
      />
    ));
  };

  const layout = {
    title: 'My Reviews',
    subtitle: REVIEWS_SUBTITLE,
    guideKey: 'reviews',
  };

  if (isLoading) {
    return (
      <ClientPageLayout {...layout}>
        <div className="space-y-5">
          <div className="h-5 w-28 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          <ListItemSkeleton count={3} />
        </div>
      </ClientPageLayout>
    );
  }

  if (error) {
    return (
      <ClientPageLayout {...layout}>
        <div className="rounded-2xl border border-red-200/80 bg-red-50 p-8 text-center dark:border-red-900/50 dark:bg-red-950/30">
          <Star className="w-12 h-12 mx-auto mb-3 text-red-300 dark:text-red-700" />
          <h3 className="text-lg font-bold text-red-900 dark:text-red-200 mb-2">Error loading reviews</h3>
          <p className="text-sm text-red-700/80 dark:text-red-400">Failed to load your reviews. Please try again later.</p>
        </div>
      </ClientPageLayout>
    );
  }

  return (
    <ClientPageLayout {...layout}>
      <div className="space-y-5">
        {reviews.length === 0 ? (
          <ClientEmptyState
            icon={Star}
            title="No reviews yet"
            description="After a completed event, share feedback to help other clients choose vendors."
            actionLabel="View my bookings"
            actionTo="/bookings"
            tips={['Open a completed booking', 'Tap Leave Review when available']}
          />
        ) : (
          <>
            <div className="flex items-baseline justify-between gap-3 px-0.5">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                Your reviews
                <span className="ml-1.5 font-normal text-gray-500 dark:text-gray-400">
                  ({reviews.length})
                </span>
              </h2>
            </div>

            <div className="space-y-3">
              {reviews.map((review) => {
                const vendorName = review.booking?.vendor?.business_name || 
                                  review.vendor?.business_name || 
                                  'Unknown Vendor';
                const serviceName = review.booking?.vendorService?.service_name || 
                                  review.booking?.vendor_service?.service_name ||
                                  'Service';
                const eventDate = review.booking?.event_date || review.booking?.created_at;
                
                return (
                  <article
                    key={review.id}
                    className="rounded-2xl border border-gray-200/80 bg-white p-4 sm:p-5 shadow-sm hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-11 h-11 bg-amber-50 dark:bg-amber-950/40 border border-amber-100 dark:border-amber-800 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Camera className="w-5 h-5 text-amber-700 dark:text-amber-300" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-gray-50 truncate">
                              {vendorName}
                            </h3>
                            {review.is_verified && (
                              <span className="px-2 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-200/80 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800/50 text-xs rounded-full font-medium">
                                Verified
                              </span>
                            )}
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full font-medium border ${
                                review.is_approved
                                  ? 'bg-sky-50 text-sky-800 border-sky-200/80 dark:bg-sky-950/40 dark:text-sky-300'
                                  : 'bg-amber-50 text-amber-800 border-amber-200/80 dark:bg-amber-950/40 dark:text-amber-300'
                              }`}
                            >
                              {review.is_approved ? 'Published' : 'Pending'}
                            </span>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2 truncate">
                            {serviceName}
                          </p>
                          {review.title && (
                            <h4 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-50 mb-1.5 line-clamp-2">
                              {review.title}
                            </h4>
                          )}
                          <div className="flex items-center gap-0.5 mb-2">
                            {renderStars(review.rating)}
                          </div>
                          {review.comment && (
                            <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 mb-2 sm:mb-3 line-clamp-3 sm:line-clamp-none leading-relaxed">
                              {review.comment}
                            </p>
                          )}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <div className="flex items-center">
                              <Calendar className="w-3.5 h-3.5 mr-1 flex-shrink-0 text-sky-600 dark:text-sky-400" />
                              <span className="truncate">Reviewed {new Date(review.created_at).toLocaleDateString()}</span>
                            </div>
                            {eventDate && (
                              <div className="flex items-center">
                                <Calendar className="w-3.5 h-3.5 mr-1 flex-shrink-0 text-sky-600 dark:text-sky-400" />
                                <span className="truncate">Event: {new Date(eventDate).toLocaleDateString()}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-end sm:justify-start gap-1 flex-shrink-0">
                        <button
                          type="button"
                          className="p-2 text-gray-400 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-950/40 !rounded-xl transition-colors"
                          title="Edit review"
                        >
                          <Edit className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteReview(review.id)}
                          disabled={deleteReviewMutation.isPending}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 !rounded-xl transition-colors disabled:opacity-50"
                          title="Delete review"
                        >
                          <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </ClientPageLayout>
  );
};

export default ClientReviews;
